import { Button, CloseButton, SaveButton } from '@ui/shared/Button';
import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { closePopup, openPopup } from '../../../data/providers/popupsProvider';
import {
  ITestsSetting,
  testsSettingProvider,
} from '../../../data/providers/settings/testsSettingProvider';
import { isValidDateRange } from '../../../data/providers/utils/isValidDateRange';
import { IRange } from '../../../data/types';
import { saveTestsSetting } from '../../../data/writers/settings/testsSetting';
import { CardBody } from '../../shared/card/CardBody';
import { CardFooter } from '../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../shared/card/CardHeader';
import { ControlSpacer } from '../../shared/form/ControlSpacer';
import { FormContainer } from '../../shared/form/FormContainer';
import { TimeframeSelect } from '../../shared/form/selects/TimeframeSelect';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import { PopupCard, PopupCardContent } from '../../shared/popups/PopupCard';

interface IPopupProps {
  onClose: () => void;
}

const Popup = (props: IPopupProps): React.ReactElement => {
  const [loading, setLoading] = React.useState<boolean>(true);
  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [dateRange, setDateRange] = React.useState<IRange>();

  /**
   * @description handles change to test setting
   * @param range
   * @returns
   */
  const changeHandler = (range: IRange): void => setDateRange(range);

  /**
   * @description subscribe to test setting
   */
  React.useEffect((): (() => void) => {
    const subscriber = testsSettingProvider().subscribe(
      (setting: ITestsSetting): void => {
        setLoading(false);
        setDateRange(setting.range);
      }
    );
    return (): void => subscriber.unsubscribe();
  }, []);

  /**
   * @description persists setting to the database
   */
  const saveHandler = async (): Promise<void> => {
    setIsSaving(true);
    await saveTestsSetting({ range: dateRange });
    setIsSaving(false);
    props.onClose();
  };

  /**
   * @description persists setting to the database
   */
  const clearHandler = async (): Promise<void> => {
    setIsSaving(true);
    setDateRange(null);
    await saveTestsSetting({ range: null });
    setIsSaving(false);
    props.onClose();
  };

  const settingIsValid = React.useMemo(
    (): boolean =>
      isValidDateRange({
        dateType: { label: 'Custom', value: 'custom' },
        dateRange,
      }),
    [JSON.stringify(dateRange)]
  );

  return (
    <PopupCard onClose={props.onClose} size='small' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft style={{ marginLeft: '10px' }}>
            <h6>Choose Date Range</h6>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          {(loading || isSaving) && <LinearLoading />}
          <FormContainer style={{ alignItems: 'flex-start' }}>
            <ControlSpacer style={{ width: '100%' }}>
              <TimeframeSelect
                onChange={changeHandler}
                dateRange={dateRange}
                mode='LOCAL'
              />
            </ControlSpacer>
          </FormContainer>
        </CardBody>
        <CardFooter surface={8}>
          <CardHeaderRight>
            <Button
              title='Cancel'
              icon='cancel'
              onClick={clearHandler}
              disabled={!settingIsValid || isSaving}
             />
            <SaveButton
              type='submit'
              onClick={saveHandler}
              disabled={isSaving || !settingIsValid}
            />
          </CardHeaderRight>
        </CardFooter>
      </PopupCardContent>
    </PopupCard>
  );
};

export const DateRangePopup = withRouter(
  (props: RouteComponentProps): React.ReactElement => {
    const open = (): void => {
      openPopup(
        <Popup onClose={closePopup(props.history)} />,
        props.history,
        'date-range'
      );
    };
    return (
      <Button onClick={open} title='Date Range' icon='calendar_month' />
    );
  }
);
